import { type Identifier } from "react-admin";
import type { Client } from "./client";
import {
  type AuditFields,
  type ClientRef,
  type PartnerRef,
  type CommonFields,
  type VendorRef,
  type TpaVendorRefs,
  type RxVendorRefs,
  CommonStatus,
} from "./common";
import type { EndorsementType } from "./endorsement";
import type { EndorsementConfigMap } from "./endorsementConfig";
import { DateYMDString } from "./date";
import type { LaseredConditionRequest } from "./claimant";

// Policy status enum
export const PolicyStatus = {
  ACTIVE: CommonStatus.ACTIVE,
  DRAFT: "draft",
  IN_PROGRESS: CommonStatus.IN_PROGRESS,
  COMPLETED: CommonStatus.COMPLETED,
  CANCELLED: CommonStatus.CANCELLED,
  EXPIRED: CommonStatus.EXPIRED,
} as const;

export type PolicyStatusType = (typeof PolicyStatus)[keyof typeof PolicyStatus];

export type ReportingCadence = "monthly" | "weekly";

// Base interface with all core fields
export interface PolicyBase {
  effective_start_date: DateYMDString;
  effective_end_date: DateYMDString;
  /** Canonical "N/M" months (ADR-081 D5.2a); legacy rows may still carry wording until 000233 ran. */
  contract_basis: string;
  status: PolicyStatusType;
  /**
   * Mid-term cancellation date (ADR-081 D5, #7320). Present iff status is
   * "cancelled"; both accumulation windows end here. effective_end_date keeps
   * the contracted term.
   */
  termination_date?: DateYMDString | null;
  aggregate_benefit: AggregateBenefit;
  specific_benefit: SpecificBenefit;
  source_data: PolicySourceData;
  is_accel_funding_enabled: boolean;
  /**
   * Manual Rx carve-out override (ADR-077 §2 / #5731). On/off toggle for the
   * exception case; null => use the value inferred from the vendor sets. Set via
   * PATCH. There is intentionally no clear-to-inferred control in the UX.
   */
  rx_carve_out_override?: boolean | null;
  reporting_cadence?: ReportingCadence | null;
  reporting_day_of_month?: number | null;
  reporting_day_of_week?: number | null;
  task_lead_days?: number | null;
  reporting_start_date?: DateYMDString | null;
}

/** Aggregate Specific Deductible status for a policy (Feature #2934, PR 2773). */
export type ASDStatus = "accumulating" | "breached" | "unbreached";

/**
 * Policy-level Aggregate Specific Deductible state, persisted on
 * `policies.asd_tracking` and computed by the ASD engine. Absent on non-ASD
 * policies — gate on `specific_benefit.is_aggregate_specific_deductible`, not on
 * the presence of this object.
 */
export interface ASDTracking {
  /** Corridor still to be absorbed before the carrier pays. */
  asd_remaining?: number;
  /** accumulating = corridor absorbing; breached = carrier paying; unbreached = fell back below (HWM active). */
  asd_status?: ASDStatus;
  /** Peak cumulative excess, latched when the total recedes back below the ASD (#3335). */
  amt_high_water_mark?: number;
  amt_carrier_total_paid?: number;
  last_calculated_doc_ref?: string;
  terminal_reconciliation_status?: string;
}

// Full Policy type returned from GET requests
export interface Policy
  extends PolicyBase, CommonFields, ClientRef, PartnerRef, VendorRef, TpaVendorRefs, RxVendorRefs, AuditFields {
  source_documents?: Identifier[];
  client?: Client;
  /** Enriched by backend — true when the policy has at least one family_deductible endorsement. */
  has_family_deductible_endorsement?: boolean;
  /** ASD corridor state (#2934). Present only on aggregate-specific-deductible policies. */
  asd_tracking?: ASDTracking;
  /**
   * Read-only, computed on read (ADR-081 D5, policy_deductible_basis_window; #7481):
   * the accumulation windows the calculation engine applies, per basis kind. Shown next
   * to the benefit dates so a user can see the run-in / run-out is applied without
   * re-keying a date. Absent when the view has no row for the policy.
   */
  deductible_basis?: DeductibleBasisWindows;
  /** Enriched by backend — the policy-period summary Note shown on the Claims Summary + Policy detail (US 5370). */
  note?: string;
  /**
   * Computed-on-read Rx carve-out signals (ADR-077 §2, backend-derived; #5788).
   * `inferred` = RX vendor set non-empty AND disjoint from TPA. `effective` =
   * override ?? inferred (drives whether per-claim-type authority engages).
   * `review_needed` = RX overlaps TPA with no override set (ambiguous — a human
   * should resolve it via the override).
   */
  rx_carve_out_inferred?: boolean;
  rx_carve_out_effective?: boolean;
  rx_carve_out_review_needed?: boolean;
}

/**
 * Shape of a new-style endorsement being configured during policy creation.
 * Each entry captures the type, enabled state, valid period, and type-specific config.
 *
 * TODO: align with finalized Go struct once POST /policies endpoint accepts
 * endorsement config alongside the policy creation payload.
 */
export interface EndorsementCreateConfig {
  type: EndorsementType;
  is_enabled: boolean;
  valid_period_start: string | null;
  valid_period_end: string | null;
  config: EndorsementConfigMap[EndorsementType];
}

/**
 * Which kind of laser an inline create-form entry represents, one per claimant
 * (PO 2026-10-07: Standard / Contract / Conditional):
 *  - `straight`: Standard, a straight laser POSTed to `/lasered-claimants`
 *    (default for back-compat with entries created before Feature #5287).
 *  - `contract`: a contract laser PUT to `.../contract-laser` (windows + CPT
 *    exclusions + elevated deductible).
 *  - `exclusion`: a full contract exclusion PUT to `.../contract-laser`
 *    (Contract with "fully exclude" on).
 *  - `conditional`: conditional lasers, after an optional standing deductible
 *    (a straight laser) when `amt_lasered_deductible` > 0.
 */
export type LaseredClaimantEntryKind = "straight" | "contract" | "exclusion" | "conditional";

/** A single lasered claimant entry for the policy creation form. */
export interface LaseredClaimantCreateEntry {
  claimant_id_ref: string;
  claimant_display_name: string; // UI-only, not sent to backend
  /** Defaults to "straight" when absent (back-compat). */
  entry_kind?: LaseredClaimantEntryKind;
  laser_type: string;
  /** The Standard deductible, or the Conditional standing deductible (0 = none). */
  amt_lasered_deductible: number;
  amt_incremental_lasered_deductible: number;
  /** Required for individual laser assignments (mgmt-api COM-000002). */
  effective_date?: string;
  /** Contract-laser fields (Feature #5287) — only relevant when entry_kind is "contract". */
  amt_contract_lasered_deductible?: number;
  contract_incurred_start_date?: DateYMDString;
  contract_incurred_end_date?: DateYMDString;
  contract_paid_start_date?: DateYMDString;
  contract_paid_end_date?: DateYMDString;
  excluded_procedures?: string[];
  /** Conditional lasers (Feature #3502) written after the policy (and standing deductible) exist. */
  lasered_conditions?: LaseredConditionRequest[];
}

// Type for creating new policies
export interface PolicyCreate extends PolicyBase, ClientRef, PartnerRef {
  /**
   * Endorsement configs set during policy creation.
   * Keyed by EndorsementType for types that have schema-driven forms.
   */
  endorsement_configs?: Partial<Record<EndorsementType, EndorsementCreateConfig>>;
  /**
   * Lasered claimants to be applied after policy creation.
   * Processed client-side — not sent to the backend in the policy payload.
   */
  lasered_claimants?: LaseredClaimantCreateEntry[];
}

// Type for updating policies
export interface PolicyUpdate extends Partial<PolicyCreate> {
  id: string;
}

export interface PolicySourceData {
  policyholder?: string;
  policyholder_contact_name?: string;
  policyholder_contact_email?: string;
  policyholder_tin?: number;
  policyholder_address?: string;
  policyholder_state?: string;
  policyholder_zip_code?: string;
  policyholder_affiliates?: string;
  state_of_issuance?: string;
  stop_loss_carrier?: string;
  stop_loss_carrier_policy_id?: string;
  third_party_administrator?: string;
  preferred_provider?: string;
  source_policy_id: string;
  policy_carrier: string;
}

export interface AggregateBenefit {
  aggregate_deductible?: number;
  aggregate_blended_premium?: number;
  is_aggregate_deductible?: boolean;
  aggregate_stop_loss_individual_claim_limit?: number;
  aggregate_family_premium?: number;
  aggregate_individual_premium?: number;
  monthly_aggregate_spend_per_composite?: number;
  aggregate_stop_loss_percentage_reimbursable: number;
  contract_basis: string;
  contract_type?: string;
  covered_composite?: number;
  families_enrolled?: number;
  individuals_enrolled?: number;
  has_medical_coverage: boolean;
  medical_provider: string;
  has_rx_coverage: boolean;
  rx_provider?: string;
  eligible_expenses_incurred_start_date: DateYMDString;
  eligible_expenses_incurred_end_date: DateYMDString;
  eligible_expenses_paid_start_date: DateYMDString;
  eligible_expenses_paid_end_date: DateYMDString;
  aggregate_employee_plus_children_enrolled?: number;
  aggregate_employee_plus_spouse_enrolled?: number;
  aggregate_employee_plus_spouse_premium?: number;
  aggregate_employee_plus_children_premium?: number;
  aggregate_composite_premium?: number;
  aggregate_individual_monthly_factor?: number;
  aggregate_employee_plus_children_monthly_factor?: number;
  aggregate_employee_plus_spouse_monthly_factor?: number;
  aggregate_family_monthly_factor?: number;
  maximum_annual_reimbursement?: number;
}

export interface SpecificBenefit {
  is_aggregate_specific_deductible?: boolean;
  aggregate_specific_deductible?: number;
  is_specific_deductible?: boolean;
  specific_monthly_premium_rate: number;
  specific_deductible?: number;
  contract_basis: string;
  specific_family_premium?: number;
  specific_individual_premium?: number;
  specific_maximum_reimbursement: number;
  specific_stop_loss_percentage_reimbursable: number;
  maximum_specific_benefit: number;
  blended_enrolled?: number;
  contract_type?: string;
  covered_composite: number;
  families_enrolled?: number;
  individuals_enrolled?: number;
  has_medical_coverage: boolean;
  medical_provider: string;
  has_rx_coverage: boolean;
  rx_provider?: string;
  eligible_expenses_incurred_start_date: DateYMDString;
  eligible_expenses_incurred_end_date: DateYMDString;
  eligible_expenses_paid_start_date: DateYMDString;
  eligible_expenses_paid_end_date: DateYMDString;
  specific_employee_plus_children_enrolled: number;
  specific_employee_plus_spouse_premium: number;
  specific_employee_plus_children_premium: number;
  specific_composite_premium: number;
}

/**
 * One basis kind's effective accumulation window as the engine applies it (ADR-081 D5.1;
 * migration 000248 rule, #7481): the term extended by N/M, or the benefit's printed bound
 * used as-is when it differs from the term. Never re-derive these in the SPA — the view
 * is the single source of the N/M arithmetic.
 */
export interface BasisWindow {
  contract_basis: string | null;
  incurred_months: number | null;
  paid_months: number | null;
  /** Terminal Liability months applied to the paid end; 0 when no enabled TL row. */
  tl_months: number | null;
  term_months: number | null;
  /** What N/M is quoted against: the term when N or M equals its months, else 12. */
  basis_base_months: number | null;
  incurred_window_start: DateYMDString | null;
  incurred_window_end: DateYMDString | null;
  /** False for bare Paid (N = 999): no incurred lookback restriction. */
  incurred_check_applies: boolean;
  paid_window_start: DateYMDString | null;
  paid_window_end: DateYMDString | null;
  /**
   * A keyed benefit bound matches neither the term nor term ± N/M (nor term + TL for the
   * paid end): a genuine disagreement to resolve with the data owner. Either keying
   * convention (dates at the term, or the printed window) is clean.
   */
  basis_mismatch: boolean;
  /** No contract basis on the benefit or the policy; 12/12 assumed. */
  basis_defaulted: boolean;
}

export interface DeductibleBasisWindows {
  specific?: BasisWindow;
  aggregate?: BasisWindow;
}
